import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "../../lib/utils"

import useOsDark from "../../hooks/useOsDark"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, style, ...props }, ref) => {
  const isDark = useOsDark();
  const container =
    typeof document !== "undefined"
      ? ((document.fullscreenElement as HTMLElement | null) ??
          (document.querySelector(".mapleos-root") as HTMLElement | null) ??
          (document.getElementById("root") as HTMLElement | null) ??
          document.body)
      : null;

  const mapleRoot = typeof document !== "undefined"
    ? (document.querySelector(".mapleos-root") as HTMLElement | null)
    : null;

  const inheritedThemeStyle = React.useMemo<React.CSSProperties>(() => {
    if (!mapleRoot || typeof window === "undefined") {
      return {};
    }

    const computed = window.getComputedStyle(mapleRoot);
    const varsToCopy = [
      "--background",
      "--foreground",
      "--card",
      "--card-foreground",
      "--popover",
      "--popover-foreground",
      "--primary",
      "--primary-foreground",
      "--primary-hover",
      "--secondary",
      "--secondary-foreground",
      "--muted",
      "--muted-foreground",
      "--accent",
      "--accent-foreground",
      "--destructive",
      "--destructive-foreground",
      "--border",
      "--input",
      "--ring",
      "--taskbar",
      "--taskbar-foreground",
      "--window-header",
      "--window-header-foreground",
      "--window-header-inactive",
      "--desktop",
      "--gradient-window",
      "--gradient-button",
      "--gradient-pressed",
      "--taskbar-btn",
      "--taskbar-btn-hover",
      "--taskbar-btn-active",
      "--taskbar-btn-foreground",
      "--window-control-bg",
      "--window-control-bg-hover",
    ];

    const themeVars: Record<string, string> = {};

    for (const variableName of varsToCopy) {
      const value = computed.getPropertyValue(variableName).trim();
      if (value) {
        themeVars[variableName] = value;
      }
    }

    return themeVars as React.CSSProperties;
  }, [mapleRoot]);

  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        style={{ ...inheritedThemeStyle, ...style }}
        className={cn(
          "mapleos-root",
          isDark ? "dark" : "",
          "relative z-[1200000] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }